/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import { Help } from '@material-ui/icons';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import PageHeader from 'components/PageHeader';
import RefreshButton from 'components/RefreshButton';
import { SearchBar } from 'components/SearchBar';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { getAllAdvisorsRequest } from 'modules/advisors/actions';
import {
  AdvisorProps,
  ListAdvisorsDataProps,
} from 'modules/advisors/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { formatCurrency } from 'utils/helpers';
import AdvisorButtons from './AdvisorButtons';
import AdvisorDetails from './AdvisorDetails';
import AdvisorsDashboard from './AdvisorsDashboard';
import headers from './headers';
import screenFields from './screenFields';
import styles, { overrideSearchbar } from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
  status: 'pending',
  advisor_type: '',
  impact: '',
  environment_type: '',
};

interface Props {
  isLoading: boolean;
  listAdvisors: ListAdvisorsDataProps;
  getAllAdvisorsRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const ListAdvisors = ({
  isLoading,
  listAdvisors,
  getAllAdvisorsRequest,
  modalOpen,
  modalClose,
}: Props) => {
  const classes = useStyles();
  const {
    query,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters(initialQuery);

  const status =
    new URLSearchParams(window.location.search).get('status') || 'pending';

  const [refresh, setRefresh] = React.useState(false);

  const getTranslatedImpact = (impact?: string) => {
    const impactMap: Record<string, { icon: JSX.Element; label: string }> = {
      low: {
        icon: <DoubleArrowIcon className={classes.lowImpact} />,
        label: i18n.t('LOW_IMPACT_KEY'),
      },
      medium: {
        icon: <DoubleArrowIcon className={classes.mediumImpact} />,
        label: i18n.t('MEDIUM_IMPACT_KEY'),
      },
      high: {
        icon: <DoubleArrowIcon className={classes.highImpact} />,
        label: i18n.t('HIGH_IMPACT_KEY'),
      },
    };

    const data = impactMap[impact ?? ''];

    return data ? (
      <span className={classes.impactGrid}>
        {data.icon}
        {data.label}
      </span>
    ) : (
      impact
    );
  };

  const getAdvisors = (refresh = false) => {
    getAllAdvisorsRequest({ query });
    setRefresh(refresh);
  };

  const handleGetAdvisor = (item: AdvisorProps) => {
    modalOpen({
      overrideClasses: {
        content: {
          padding: 12,
        },
      },
      title: i18n.t('ADVISOR_PAGE.OPTIMIZATION_DETAILS_KEY'),
      maxWidth: 'md',
      footer: (
        <AdvisorButtons
          onClose={modalClose}
          isLoading={isLoading}
          advisorUuid={item?.advisor_uuid}
          getAdvisors={getAdvisors}
        />
      ),
      content: (
        <AdvisorDetails
          getTranslatedImpact={getTranslatedImpact}
          advisorUuid={item?.advisor_uuid}
        />
      ),
    });
  };

  React.useEffect(() => {
    getAdvisors();
  }, [getAllAdvisorsRequest, query]);

  React.useEffect(() => {
    setQuery(prev => ({ ...prev, status }));
  }, [status]);

  const openAdvisorTypeExplanation = () => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.ADVISOR_TYPE_EXPLANATION_BUTTON_KEY'),
      maxWidth: 'md',
      content: (
        <>
          <Grid container className={classes.advisorTypeExplanations}>
            <Typography>
              {i18n.t('ADVISOR_PAGE.ADVISOR_TYPE_EXPLANATION_KEY')}
            </Typography>
            <Grid style={{ marginTop: 8 }}>
              <Grid className={classes.advisorTypeExplanationGrid}>
                {'• '}
                <Typography className={classes.advisorType}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_WEEKDAY_KEY')}
                </Typography>
                <Typography>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_WEEKDAY_EXPLANATION_KEY')}
                </Typography>
              </Grid>

              <Grid className={classes.advisorTypeExplanationGrid}>
                {'• '}
                <Typography className={classes.advisorType}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_SATURDAY_KEY')}
                </Typography>
                <Typography>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_SATURDAY_EXPLANATION_KEY')}
                </Typography>
              </Grid>

              <Grid className={classes.advisorTypeExplanationGrid}>
                {'• '}
                <Typography className={classes.advisorType}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_SUNDAY_KEY')}
                </Typography>
                <Typography>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_SUNDAY_EXPLANATION_KEY')}
                </Typography>
              </Grid>

              <Grid className={classes.advisorTypeExplanationGrid}>
                {'• '}
                <Typography className={classes.advisorType}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_ANYDAY_KEY')}
                </Typography>
                <Typography>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_ANYDAY_EXPLANATION_KEY')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </>
      ),
    });
  };

  return (
    <Grid container>
      <PageHeader
        title={i18n.t('ADVISOR_PAGE.LIST_ADVISORS_TITLE_KEY')}
        subtitle={i18n.t('ADVISOR_PAGE.LIST_ADVISORS_SUBTITLE_KEY')}
        rightSideComponent={
          <Grid item className={classes.headerIcons}>
            <RefreshButton
              id="btn-refresh"
              onClick={() => getAdvisors(true)}
              animate={isLoading}
              className={classes.refreshButton}
              disabled={isLoading}
            />
            <Button
              onClick={openAdvisorTypeExplanation}
              variant="outlined"
              startIcon={<Help fontSize="small" />}
              className={classes.advisorTypeExplanationButton}
            >
              {i18n.t('ADVISOR_PAGE.ADVISOR_TYPE_EXPLANATION_BUTTON_KEY')}
            </Button>
          </Grid>
        }
      />

      <AdvisorsDashboard query={query} />

      <Grid container className={classes.content}>
        <Formik
          initialValues={{
            status: query.status || 'all',
            impact: query.impact || 'all',
            advisor_type: query.advisor_type || 'all',
            environment_type: query.environment_type || 'all',
          }}
          onSubmit={values => setQuery(prev => ({ ...prev, ...values }))}
          enableReinitialize
        >
          {({ setFieldValue, errors, values }) => (
            <Grid
              container
              xs={12}
              justify="space-between"
              style={{ marginTop: 32, marginBottom: 16, alignItems: 'center' }}
            >
              <Grid item xs={5}>
                <SearchBar
                  id="search-advisor"
                  isLoading={isLoading}
                  handleSearch={handleSearch}
                  setSearchValue={setSearchValue}
                  overrideClasses={overrideSearchbar}
                />
              </Grid>
              <Grid container xs={7} spacing={2}>
                <GenericForm
                  fields={values}
                  errors={errors}
                  screenFields={screenFields}
                  setFieldValue={(key, value) => {
                    setFieldValue(key, value);
                    setQuery(prev => ({
                      ...prev,
                      offset: 0,
                      [key]: value === 'all' ? '' : value,
                    }));
                  }}
                  overrideClasses={{
                    selectLabel: { fontSize: 12, fontWeight: 500 },
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Formik>

        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  style={item?.style}
                  centerTitle={item?.centerTitle}
                  markOderItem={itemOrder === item?.key}
                  order={item?.order}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: AdvisorProps) => (
            <TableRow
              key={item?.advisor_uuid}
              onClick={() => handleGetAdvisor(item)}
            >
              <TableCell
                tooltip={item?.partner?.partner_name}
                className={classes.longNameField}
                ellipsis
              >
                {item?.partner?.partner_name}
              </TableCell>
              <TableCell
                tooltip={item?.environment?.environment_name}
                className={classes.longNameField}
                ellipsis
              >
                {item?.environment?.environment_name}
              </TableCell>
              <TableCell centerTitle>
                {i18n.t(
                  `ADVISOR_PAGE.${item?.environment?.environment_type?.toUpperCase()}_KEY`
                )}
              </TableCell>
              <TableCell centerTitle>
                {i18n.t(`${item?.advisor_type?.toUpperCase()}_KEY`)}
              </TableCell>
              <TableCell centerTitle>
                {format(new Date(item?.created_at), 'dd/MM/yyyy')}
              </TableCell>
              <TableCell>
                {!!item?.estimated_monthly_cost_savings &&
                  formatCurrency(item?.estimated_monthly_cost_savings)}
              </TableCell>
              <TableCell>{getTranslatedImpact(item?.impact)}</TableCell>
              <TableCell centerTitle>
                {i18n.t(`${item?.status?.toUpperCase()}_KEY`)}
              </TableCell>
            </TableRow>
          )}
          data={listAdvisors}
          isLoading={!refresh && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('NO_ADVISOR_KEY')}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  advisors: { isLoading, listAdvisors },
}: ApplicationState) => ({
  isLoading,
  listAdvisors,
});

export default connect(mapStateToProps, {
  getAllAdvisorsRequest,
  modalOpen,
  modalClose,
})(ListAdvisors);
